import React, { createContext, useContext, useReducer } from 'react';

// Initial state
const initialState = {
  // UI State
  sidebarOpen: false,
  theme: 'light',
  loading: false,
  error: null,
  
  // Data State
  campaigns: [],
  summaryStats: null,
  lastUpdated: null,
  
  // Filter State
  globalFilters: {
    dateRange: 'all',
    searchTerm: '',
    sortBy: 'date',
    sortOrder: 'desc'
  },
  
  // User preferences
  preferences: {
    autoRefresh: false,
    refreshInterval: 30000, // 30 seconds
    compactView: false,
    showCharts: true
  }
};

// Action types
const ActionTypes = {
  // UI Actions
  TOGGLE_SIDEBAR: 'TOGGLE_SIDEBAR',
  SET_THEME: 'SET_THEME',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  
  // Data Actions
  SET_CAMPAIGNS: 'SET_CAMPAIGNS',
  SET_SUMMARY_STATS: 'SET_SUMMARY_STATS',
  UPDATE_LAST_UPDATED: 'UPDATE_LAST_UPDATED',
  
  // Filter Actions
  SET_GLOBAL_FILTERS: 'SET_GLOBAL_FILTERS',
  RESET_FILTERS: 'RESET_FILTERS',
  
  // Preference Actions
  SET_PREFERENCES: 'SET_PREFERENCES',
  TOGGLE_AUTO_REFRESH: 'TOGGLE_AUTO_REFRESH',
  SET_REFRESH_INTERVAL: 'SET_REFRESH_INTERVAL'
};

// Reducer
const appReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.TOGGLE_SIDEBAR:
      return {
        ...state,
        sidebarOpen: !state.sidebarOpen
      };
      
    case ActionTypes.SET_THEME:
      return {
        ...state,
        theme: action.payload
      };
      
    case ActionTypes.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };
      
    case ActionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false
      };
      
    case ActionTypes.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
      
    case ActionTypes.SET_CAMPAIGNS:
      return {
        ...state,
        campaigns: action.payload,
        lastUpdated: new Date().toISOString(),
        loading: false,
        error: null
      };
      
    case ActionTypes.SET_SUMMARY_STATS:
      return {
        ...state,
        summaryStats: action.payload
      };
      
    case ActionTypes.UPDATE_LAST_UPDATED:
      return {
        ...state,
        lastUpdated: new Date().toISOString()
      };
      
    case ActionTypes.SET_GLOBAL_FILTERS:
      return {
        ...state,
        globalFilters: {
          ...state.globalFilters,
          ...action.payload
        }
      };
      
    case ActionTypes.RESET_FILTERS:
      return {
        ...state,
        globalFilters: initialState.globalFilters
      };
      
    case ActionTypes.SET_PREFERENCES:
      return {
        ...state,
        preferences: {
          ...state.preferences,
          ...action.payload
        }
      };
      
    case ActionTypes.TOGGLE_AUTO_REFRESH:
      return {
        ...state,
        preferences: {
          ...state.preferences,
          autoRefresh: !state.preferences.autoRefresh
        }
      };
      
    case ActionTypes.SET_REFRESH_INTERVAL:
      return {
        ...state,
        preferences: {
          ...state.preferences,
          refreshInterval: action.payload
        }
      };
      
    default:
      return state;
  }
};

// Create context
const AppContext = createContext();

// Context provider
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Action creators
  const actions = {
    // UI Actions
    toggleSidebar: () => dispatch({ type: ActionTypes.TOGGLE_SIDEBAR }),
    setTheme: (theme) => dispatch({ type: ActionTypes.SET_THEME, payload: theme }),
    setLoading: (loading) => dispatch({ type: ActionTypes.SET_LOADING, payload: loading }),
    setError: (error) => dispatch({ type: ActionTypes.SET_ERROR, payload: error }),
    clearError: () => dispatch({ type: ActionTypes.CLEAR_ERROR }),
    
    // Data Actions
    setCampaigns: (campaigns) => dispatch({ type: ActionTypes.SET_CAMPAIGNS, payload: campaigns }),
    setSummaryStats: (stats) => dispatch({ type: ActionTypes.SET_SUMMARY_STATS, payload: stats }),
    updateLastUpdated: () => dispatch({ type: ActionTypes.UPDATE_LAST_UPDATED }),
    
    // Filter Actions
    setGlobalFilters: (filters) => dispatch({ type: ActionTypes.SET_GLOBAL_FILTERS, payload: filters }),
    resetFilters: () => dispatch({ type: ActionTypes.RESET_FILTERS }),
    
    // Preference Actions
    setPreferences: (preferences) => dispatch({ type: ActionTypes.SET_PREFERENCES, payload: preferences }),
    toggleAutoRefresh: () => dispatch({ type: ActionTypes.TOGGLE_AUTO_REFRESH }),
    setRefreshInterval: (interval) => dispatch({ type: ActionTypes.SET_REFRESH_INTERVAL, payload: interval })
  };

  const value = {
    state,
    actions,
    // Computed values
    isLoading: state.loading,
    hasError: !!state.error,
    hasCampaigns: state.campaigns.length > 0,
    isDarkTheme: state.theme === 'dark'
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook to use the context
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

// Export action types for external use
export { ActionTypes };